import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

/**
 * A search box whose value is debounced *and* mirrored into `?q=`.
 *
 * The naive version of this loops. Writing the URL on every keystroke changes
 * `useSearchParams()`, which fires the effect that reads the URL back, which
 * sets the debounced value immediately — so the debounce is defeated and the
 * screen issues one request per character while appearing to be debounced.
 *
 * Two things stop that here:
 *
 *  - **The URL is written from the debounced value, not from the keystroke.**
 *    That is also better behaviour on its own: the address bar updates at the
 *    same cadence as the query rather than churning per character.
 *  - **A ref marks our own write**, so the read-back effect ignores it. The
 *    effect still exists because the URL genuinely does change from outside —
 *    a link, or the browser's back button, must land in the box.
 *
 * Returns the live `term` for the input and the settled `debounced` for the
 * query key.
 */
export function useUrlSearch(paramName = "q", delayMs = 250) {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get(paramName) ?? "";

  const [term, setTerm] = useState(fromUrl);
  const [debounced, setDebounced] = useState(fromUrl);

  const isOwnWrite = useRef(false);
  // Held in a ref because react-router does not guarantee a stable identity for
  // `setParams`; depending on it directly would re-arm the timer every render.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  useEffect(() => {
    if (isOwnWrite.current) {
      isOwnWrite.current = false;
      return;
    }
    setTerm(fromUrl);
    setDebounced(fromUrl);
  }, [fromUrl]);

  useEffect(() => {
    if (term === debounced) return;
    const id = setTimeout(() => {
      setDebounced(term);
      isOwnWrite.current = true;
      setParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (term) next.set(paramName, term);
          else next.delete(paramName);
          return next;
        },
        // `replace` so typing does not fill the history stack with every
        // intermediate search.
        { replace: true },
      );
    }, delayMs);
    return () => clearTimeout(id);
  }, [term, debounced, paramName, delayMs]);

  return { term, setTerm, debounced };
}
